import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Descriptions, Drawer, Image, List, Spin, Typography } from 'antd';
import { getMealPlannerCategory, getMealPlannerRecipe } from '@/Axios/api/mealPlanner';
import { MealPlannerCategoryRow, MealPlannerRecipeDetail } from '@/Interfaces/I_mealPlanner';

export default function Details({
  kind,
  id,
  onClose,
}: {
  kind: 'categories' | 'recipes';
  id?: string;
  onClose: () => void;
}) {
  const [category, setCategory] = useState<MealPlannerCategoryRow>();
  const [recipe, setRecipe] = useState<MealPlannerRecipeDetail>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    const controller = new AbortController();
    setCategory(undefined);
    setRecipe(undefined);
    setError('');
    if (id) {
      setLoading(true);
      const load = async () => {
        try {
          if (kind === 'categories') {
            const response = await getMealPlannerCategory(id, controller.signal);
            if (!controller.signal.aborted) setCategory(response.data);
          } else {
            const response = await getMealPlannerRecipe(id, controller.signal);
            if (!controller.signal.aborted) setRecipe(response.data.recipe);
          }
        } catch {
          if (!controller.signal.aborted) setError('Unable to load details. Close and try again.');
        } finally {
          if (!controller.signal.aborted) setLoading(false);
        }
      };
      load();
    }
    return () => controller.abort();
  }, [kind, id]);
  const status = (disabled: boolean | null) =>
    disabled == null ? 'Unknown' : disabled ? 'Disabled' : 'Enabled';
  return (
    <Drawer
      title={`Meal Planner ${kind === 'categories' ? 'category' : 'recipe'}: ${id || ''}`}
      open={!!id}
      onClose={onClose}
      width={720}
    >
      {loading && <Spin />}
      {error && <Alert type="error" message={error} />}
      {category && (
        <>
          {category.category.iconUrl && <Image width={80} src={category.category.iconUrl} />}
          <Descriptions
            bordered
            column={1}
            items={[
              { key: 'name', label: 'Name', children: category.category.name || '—' },
              { key: 'type', label: 'Type', children: category.category.type || '—' },
              { key: 'status', label: 'Status', children: status(category.category.disabled) },
              { key: 'source', label: 'Source', children: category.category.source || '—' },
              {
                key: 'ingredient',
                label: 'Ingredient ID',
                children: category.category.ingredientId || '—',
              },
              {
                key: 'group',
                label: 'Ingredient group ID',
                children: category.category.ingredientGroupId || '—',
              },
              {
                key: 'count',
                label: 'Assigned / active recipes',
                children: `${category.totalRecipeCount} / ${category.activeRecipeCount}`,
              },
              {
                key: 'ids',
                label: 'Recipe IDs',
                children: (category.category.recipeIds || []).join(', ') || '—',
              },
            ]}
          />
          <Link
            to={`/meal-planner/recipes?categoryId=${encodeURIComponent(
              category.category.categoryId,
            )}`}
          >
            View assigned recipes
          </Link>
        </>
      )}
      {recipe && (
        <>
          {recipe.heroImageUrl && <Image width={200} src={recipe.heroImageUrl} />}
          <Descriptions
            bordered
            column={1}
            items={[
              { key: 'name', label: 'Name', children: recipe.name || '—' },
              { key: 'status', label: 'Status', children: status(recipe.disabled) },
              {
                key: 'prep',
                label: 'Preparation time / seconds',
                children: `${recipe.preparationTimeDisplayText || '—'} / ${
                  recipe.preparationTime ?? '—'
                }`,
              },
              {
                key: 'cook',
                label: 'Cooking time / seconds',
                children: `${recipe.cookingTimeDisplayText || '—'} / ${recipe.cookingTime ?? '—'}`,
              },
              {
                key: 'total',
                label: 'Total time / seconds',
                children: `${recipe.totalTimeDisplayText || '—'} / ${recipe.totalTime ?? '—'}`,
              },
              { key: 'difficulty', label: 'Difficulty', children: recipe.difficulty ?? '—' },
              { key: 'count', label: 'Ingredient count', children: recipe.ingredientCount ?? '—' },
              {
                key: 'servings',
                label: 'Serving options',
                children: (recipe.servingOptions || []).join(', ') || '—',
              },
              {
                key: 'default',
                label: 'Default serving option',
                children: recipe.defaultServingOption || '—',
              },
              {
                key: 'ingredients',
                label: 'Ingredient IDs',
                children: (recipe.ingredientIds || []).join(', ') || '—',
              },
              {
                key: 'related',
                label: 'Related recipe IDs',
                children: (recipe.relatedRecipeIds || []).join(', ') || '—',
              },
            ]}
          />
          {(recipe.ingredientSections || []).map((section, index) => (
            // eslint-disable-next-line react/no-array-index-key
            <section key={`${section.sectionId}-${index}`}>
              <Typography.Title level={5}>
                {section.title} ({section.sectionCode})
              </Typography.Title>
              <Typography.Text type="secondary">
                {section.sectionId} / parent: {section.parentSectionId || '—'}
              </Typography.Text>
              <List
                dataSource={section.items || []}
                renderItem={(item) => (
                  <List.Item>
                    <div>
                      {item.displayName} ({item.ingredientId || 'No mapped ingredient'})
                      {(item.quantities || []).map((quantity, quantityIndex) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={`${quantity.servingOption}-${quantityIndex}`}>
                          {quantity.servingOption}: {quantity.displayText}
                        </div>
                      ))}
                    </div>
                  </List.Item>
                )}
              />
            </section>
          ))}
          <Typography.Title level={5}>Cooking steps</Typography.Title>
          <List
            dataSource={recipe.cookingSteps || []}
            renderItem={(step) => (
              <List.Item>
                {step.step}. {step.description}
              </List.Item>
            )}
          />
          <Typography.Title level={5}>Other images</Typography.Title>
          {recipe.badgeImageUrl && <Image width={80} src={recipe.badgeImageUrl} />}
          {recipe.cookingMethodIconUrl && <Image width={40} src={recipe.cookingMethodIconUrl} />}
        </>
      )}
    </Drawer>
  );
}
